// Campus campus-web features/ai/lib 에서 옮겨 왔다(PICNIC-MB-FIELD-001 §6 3단계 — 놀이터 진짜 카메라). 고칠 때는 두 곳을 함께.
/**
 * tf.js MobileNet + KNN 분류기(PAI-04 정확도 경로). <b>같은 {@link Classifier} 얼굴</b> — 화면은 이걸 쓰는지
 * 폴백을 쓰는지 모른다. MobileNet 특징 추출 + KNN(역전파 없음)이라 '학습' 은 샘플당 순전파 1회다(≤30초 쉽게 충족).
 *
 * <p><b>지연 로드·CDN.</b> tf.js·mobilenet·knn 은 <b>지원 기기에서만·처음 쓸 때</b> 받는다({@link loadTf}). 오프라인·
 * 미지원·차단이면 로드가 실패하고, 팩토리가 무의존 폴백으로 떨어진다. <b>⚠ 실기기·GPU 검증 전</b>(브라우저 필요).
 */
import type { Classifier, Frame, Prediction, SavedModel } from "./classifier";

/** tf.js 묶음 — CDN 전역에서 온다. 타입은 우리가 쓰는 것만 최소로 둔다(의존성·번들 없음). */
export type TfBundle = {
  mobilenet: { infer: (input: Frame, embedding?: boolean) => TfTensor };
  knn: {
    addExample: (t: TfTensor, label: string) => void;
    predictClass: (t: TfTensor) => Promise<{ label: string; confidences: Record<string, number> }>;
    clearClass: (label: string) => void;
    getClassExampleCount: () => Record<string, number>;
    getNumClasses: () => number;
    getClassifierDataset: () => Record<string, TfTensor>;
    setClassifierDataset: (d: Record<string, TfTensor>) => void;
  };
  /** 저장본(number[][])을 KNN 데이터셋으로 되살릴 때 쓴다. */
  tensor2d: (data: number[][]) => TfTensor;
};
type TfTensor = { dispose: () => void; arraySync?: () => number[][] };

export class TfClassifier implements Classifier {
  readonly kind = "tf" as const;
  private readonly tf: TfBundle;
  constructor(tf: TfBundle) { this.tf = tf; }

  async addSample(label: string, frame: Frame): Promise<void> {
    const e = this.tf.mobilenet.infer(frame, true);
    this.tf.knn.addExample(e, label);
    e.dispose();
  }
  async classify(frame: Frame): Promise<Prediction> {
    if (this.tf.knn.getNumClasses() === 0) return { label: "", confidence: 0 };
    const e = this.tf.mobilenet.infer(frame, true);
    try {
      const r = await this.tf.knn.predictClass(e);
      return { label: r.label, confidence: r.confidences[r.label] ?? 0 };
    } finally { e.dispose(); }
  }
  forget(label: string): void { this.tf.knn.clearClass(label); }
  labels(): string[] { return Object.keys(this.tf.knn.getClassExampleCount()); }
  sampleCount(label: string): number { return this.tf.knn.getClassExampleCount()[label] ?? 0; }
  meta(): { classes: number; samples: Record<string, number> } {
    const samples = this.tf.knn.getClassExampleCount();
    return { classes: Object.keys(samples).length, samples: { ...samples } };
  }

  serialize(): SavedModel {
    const ds = this.tf.knn.getClassifierDataset();
    const data: Record<string, number[][]> = {};
    for (const [label, t] of Object.entries(ds)) data[label] = t.arraySync?.() ?? [];
    return { kind: "tf", data };
  }
  /** 저장본으로 되살린다 — tf 묶음(같은 특징 공간)이 있어야 한다. */
  static restore(tf: TfBundle, data: unknown): TfClassifier {
    const c = new TfClassifier(tf);
    const src = (data ?? {}) as Record<string, number[][]>;
    const ds: Record<string, TfTensor> = {};
    for (const [label, arr] of Object.entries(src)) if (arr.length) ds[label] = tf.tensor2d(arr);
    if (Object.keys(ds).length) tf.knn.setClassifierDataset(ds);
    return c;
  }
}

/** CDN 전역(window.tf/mobilenet/knnClassifier). 스크립트가 실려야 존재한다. */
type Win = typeof globalThis & {
  tf?: { ready: () => Promise<void>; getBackend: () => string; tensor2d: (d: number[][]) => { dispose: () => void; arraySync?: () => number[][] } };
  mobilenet?: { load: (o?: { version?: number; alpha?: number }) => Promise<TfBundle["mobilenet"]> };
  knnClassifier?: { create: () => TfBundle["knn"] };
};

const CDN = [
  "https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.22.0/dist/tf.min.js",
  "https://cdn.jsdelivr.net/npm/@tensorflow-models/mobilenet@2.1.1/dist/mobilenet.min.js",
  "https://cdn.jsdelivr.net/npm/@tensorflow-models/knn-classifier@1.2.4/dist/knn-classifier.min.js",
];

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const el = document.createElement("script");
    el.src = src; el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`load 실패: ${src}`));
    document.head.appendChild(el);
  });
}

/**
 * tf.js 묶음을 준비한다 — 지원·온라인이면 MobileNet 을 받아 특징 추출기로, KNN 을 새로 만든다.
 * 백엔드(WebGL/WASM)가 안 서거나 스크립트가 안 실리면 <b>던진다</b> → 팩토리가 폴백한다.
 */
export async function loadTf(win: Win = globalThis as Win): Promise<TfBundle> {
  for (const src of CDN) if (!alreadyLoaded(src, win)) await loadScript(src);   // 순서대로(tf 먼저)
  if (!win.tf || !win.mobilenet || !win.knnClassifier) throw new Error("tf.js 전역이 없어요");
  await win.tf.ready();
  const backend = win.tf.getBackend();
  if (backend !== "webgl" && backend !== "wasm") throw new Error(`쓸 만한 백엔드가 없어요: ${backend}`);
  const mobilenet = await win.mobilenet.load({ version: 2, alpha: 0.5 });   // 작게 — 저사양 태블릿 여유
  return { mobilenet, knn: win.knnClassifier.create(), tensor2d: (d) => (win.tf!.tensor2d(d)) };
}

function alreadyLoaded(src: string, win: Win): boolean {
  if (src.includes("tfjs") && win.tf) return true;
  if (src.includes("mobilenet") && win.mobilenet) return true;
  if (src.includes("knn") && win.knnClassifier) return true;
  return false;
}
